import React, { useEffect, useRef } from "react";
import { View, StyleSheet, Animated, Easing, Image } from "react-native";
import { AppColors } from "../utility/AppColors";
import { AppImages } from "../utility/AppImages";

export default function RedirectLoader() {
  // Animation values
  const logoScale = useRef(new Animated.Value(1)).current;
  const logoOpacity = useRef(new Animated.Value(1)).current;
  const outerRingScale = useRef(new Animated.Value(1.6)).current;
  const outerRingRotate = useRef(new Animated.Value(0)).current;
  const outerRingOpacity = useRef(new Animated.Value(0.25)).current;
  const innerRingScale = useRef(new Animated.Value(1)).current;
  const innerRingRotate = useRef(new Animated.Value(0)).current;
  const innerRingOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Logo pulsing animation
    const logoAnimation = Animated.loop(
      Animated.sequence([
        Animated.parallel([
          Animated.timing(logoScale, {
            toValue: 0.9,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(logoOpacity, {
            toValue: 0.48,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
        Animated.parallel([
          Animated.timing(logoScale, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(logoOpacity, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
        Animated.delay(1000),
      ])
    );

    // Outer ring animation
    const outerRingAnimation = Animated.loop(
      Animated.parallel([
        Animated.sequence([
          Animated.timing(outerRingScale, {
            toValue: 1,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
          Animated.timing(outerRingScale, {
            toValue: 1.6,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]),
        Animated.sequence([
          Animated.timing(outerRingRotate, {
            toValue: 1,
            duration: 3200,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]),
        Animated.sequence([
          Animated.timing(outerRingOpacity, {
            toValue: 1,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
          Animated.timing(outerRingOpacity, {
            toValue: 0.25,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]),
      ])
    );

    // Inner ring animation
    const innerRingAnimation = Animated.loop(
      Animated.parallel([
        Animated.sequence([
          Animated.timing(innerRingScale, {
            toValue: 1.2,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
          Animated.timing(innerRingScale, {
            toValue: 1,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]),
        Animated.sequence([
          Animated.timing(innerRingRotate, {
            toValue: 1,
            duration: 3200,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]),
        Animated.sequence([
          Animated.timing(innerRingOpacity, {
            toValue: 0.25,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
          Animated.timing(innerRingOpacity, {
            toValue: 1,
            duration: 1600,
            easing: Easing.linear,
            useNativeDriver: true,
          }),
        ]),
      ])
    );

    logoAnimation.start();
    outerRingAnimation.start();
    innerRingAnimation.start();

    return () => {
      logoAnimation.stop();
      outerRingAnimation.stop();
      innerRingAnimation.stop();
    };
  }, []);

  const outerRingRotateInterpolate = outerRingRotate.interpolate({
    inputRange: [0, 1],
    outputRange: ["270deg", "0deg"],
  });

  const innerRingRotateInterpolate = innerRingRotate.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "270deg"],
  });

  return (
    <View style={styles.container}>
      {/* Outer Ring */}
      <Animated.View
        style={[
          styles.ring,
          styles.outerRing,
          {
            transform: [
              { scale: outerRingScale },
              { rotate: outerRingRotateInterpolate },
            ],
            opacity: outerRingOpacity,
          },
        ]}
      />

      {/* Inner Ring */}
      <Animated.View
        style={[
          styles.ring,
          styles.innerRing,
          {
            transform: [
              { scale: innerRingScale },
              { rotate: innerRingRotateInterpolate },
            ],
            opacity: innerRingOpacity,
          },
        ]}
      />

      {/* Logo */}
      <Animated.View
        style={[
          styles.logoContainer,
          {
            transform: [{ scale: logoScale }],
            opacity: logoOpacity,
          },
        ]}
      >
        <Image source={AppImages.KO_LOGO_Main} style={styles.logo} />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: AppColors.black_121212,
    position: "relative",
  },
  logoContainer: {
    width: 80,
    height: 80,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 3,
  },
  logo: {
    width: 80,
    height: 80,
    resizeMode: "contain",
  },
  ring: {
    position: "absolute",
    borderWidth: 3,
    borderRadius: 999,
  },
  outerRing: {
    width: 140,
    height: 140,
    borderColor: `${AppColors.appViolentColor_6D08C3}40`, // 25% opacity
    borderWidth: 3,
  },
  innerRing: {
    width: 160,
    height: 160,
    borderColor: `${AppColors.appViolentColor_6D08C3}40`, // 25% opacity
    borderWidth: 8,
  },
});
